/**
 * Debug Utility
 * 
 * Controlled logging that can be enabled/disabled via environment variable.
 * Set VITE_DEBUG=true in your .env file to enable debug logging.
 * 
 * This is a good practice because:
 * 1. No console.log statements in production 
 * 2. Easily toggle debugging during development
 * 3. Consistent logging format across the app
 * 4. Can be extended with log levels, remote logging, etc.
 */

const IS_DEBUG = import.meta.env.VITE_DEBUG === 'true' || import.meta.env.DEV;

export const debug = {
    /**
     * Log general debug info
     */
    log: (...args) => {
        if (IS_DEBUG) {
            console.log('[DEBUG]', ...args);
        }
    },

    /**
     * Log with a specific module/component tag
     */
    module: (moduleName) => ({
        log: (...args) => {
            if (IS_DEBUG) {
                console.log(`[${moduleName}]`, ...args);
            }
        },
        warn: (...args) => {
            if (IS_DEBUG) {
                console.warn(`[${moduleName}]`, ...args);
            }
        },
        error: (...args) => {
            // Errors are always logged
            console.error(`[${moduleName}]`, ...args);
        },
        info: (...args) => {
            if (IS_DEBUG) {
                console.info(`[${moduleName}]`, ...args);
            }
        },
        table: (data) => {
            if (IS_DEBUG) {
                console.log(`[${moduleName}] Table:`);
                console.table(data);
            }
        },
        group: (label, fn) => {
            if (IS_DEBUG) {
                console.group(`[${moduleName}] ${label}`);
                fn();
                console.groupEnd();
            }
        }
    }),

    /**
     * Log API request details
     */
    api: {
        request: (config) => {
            if (IS_DEBUG) {
                console.groupCollapsed(`%c[API Request] ${config.method?.toUpperCase()} ${config.url}`, 'color: #0066cc; font-weight: bold;');
                console.log('URL:', config.baseURL ? `${config.baseURL}${config.url}` : config.url);
                console.log('Method:', config.method?.toUpperCase());
                console.log('Params:', config.params);
                console.log('Data (Payload):', config.data);
                console.log('Headers:', config.headers);
                console.groupEnd();
            }
        },
        response: (response) => {
            if (IS_DEBUG) {
                console.groupCollapsed(`%c[API Response] ${response.status} ${response.config.url}`, 'color: #00cc66; font-weight: bold;');
                console.log('Status:', response.status);
                console.log('Data:', response.data);
                console.log('Headers:', response.headers);
                console.log('Config:', response.config);
                console.groupEnd();
            }
        },
        error: (error) => {
            // API errors are always logged
            console.error('%c[API Error]', 'color: #cc0000; font-weight: bold;', {
                url: error.config?.url,
                method: error.config?.method?.toUpperCase(),
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
        }
    },

    /**
     * Check if debug mode is enabled
     */
    isEnabled: () => IS_DEBUG
};

export default debug;
